export type Offer = {
  slug: string;
  brand: string;
  tagline: string;
  heroPitch: string;
  imagePath: string;
  rating: number; // 0..5
  welcome: {
    headline: string;
    details: string;
    maxEur: number;
    minDepositEur: number;
    steps: string[];
  };
  payouts: {
    speedLabel: string;
    typicalHours: number;
    methods: string[];
  };
  sports: string[];
  bestFor: string[];
  pros: string[];
  cons: string[];
  charts: {
    oddsBySport: Array<{ label: string; score: number }>;
    payoutTimeline: Array<{ label: string; hours: number }>;
    uxScore: number;
    trustScore: number;
    promoScore: number;
  };
  links: {
    official?: string;
  };
  updatedAtISO: string;
};

export const offers: Offer[] = [
  {
    slug: 'netbet',
    brand: 'NetBet',
    tagline: 'Offre simple, activation rapide, prise en main clean',
    heroPitch:
      'NetBet joue la carte du “je clique, je comprends, je parie”. Bonus lisible, parcours fluide et une expérience qui ne te fait pas perdre du temps.',
    imagePath: '/images/offers/netbet.webp',
    rating: 4.4,
    welcome: {
      headline: 'Bonus de bienvenue jusqu’à 100€',
      details:
        'Un bonus pensé pour démarrer sans friction : conditions annoncées clairement, et un démarrage sans menus inutiles.',
      maxEur: 100,
      minDepositEur: 10,
      steps: [
        'Créer un compte et confirmer tes infos',
        'Déposer à partir de 10€',
        'Activer le bonus et placer tes premiers paris',
      ],
    },
    payouts: {
      speedLabel: 'Rapide sur les méthodes populaires',
      typicalHours: 24,
      methods: ['Carte bancaire', 'Virement', 'Portefeuilles électroniques (selon dispo)'],
    },
    sports: ['Football', 'Tennis', 'Rugby', 'Basket', 'Cyclisme'],
    bestFor: ['Débutants', 'Parieurs multi-sports', 'Ceux qui veulent un site clair'],
    pros: [
      'Interface sobre, pas de “bruit”',
      'Bonus facile à comprendre',
      'Bon équilibre cotes / stabilité',
    ],
    cons: [
      'Certaines promos sont ponctuelles : il faut surveiller le calendrier',
      'Catalogue parfois plus “mainstream” que “niche”',
    ],
    charts: {
      oddsBySport: [
        { label: 'Foot', score: 86 },
        { label: 'Tennis', score: 83 },
        { label: 'Rugby', score: 80 },
        { label: 'Basket', score: 79 },
        { label: 'Cyclisme', score: 77 },
      ],
      payoutTimeline: [
        { label: 'Demande', hours: 0 },
        { label: 'Traitement', hours: 6 },
        { label: 'Validation', hours: 14 },
        { label: 'Crédité', hours: 24 },
      ],
      uxScore: 92,
      trustScore: 88,
      promoScore: 82,
    },
    links: {
      official: 'https://www.netbet.fr/',
    },
    updatedAtISO: '2025-12-17',
  },
  {
    slug: 'genybet',
    brand: 'Genybet',
    tagline: 'Un profil “parieur régulier” : promos + profondeur',
    heroPitch:
      'Genybet vise les joueurs qui veulent de la matière : promos qui tournent, profondeur de marché, et une ambiance plus “club” que vitrine.',
    imagePath: '/images/offers/genybet.webp',
    rating: 4.2,
    welcome: {
      headline: 'Jusqu’à 100€ en paris remboursés (selon périodes)',
      details:
        'Les meilleures conditions sont souvent liées aux événements. L’astuce : viser le bon timing et lire les limites de remboursement.',
      maxEur: 100,
      minDepositEur: 10,
      steps: [
        'Créer un compte + vérification KYC',
        'Faire un dépôt minimum',
        'Placer un pari éligible pour le remboursement',
      ],
    },
    payouts: {
      speedLabel: 'Correct, surtout quand le dossier est déjà vérifié',
      typicalHours: 36,
      methods: ['Carte bancaire', 'Virement', 'Solutions en ligne (selon dispo)'],
    },
    sports: ['Football', 'Tennis', 'Rugby', 'Handball', 'eSport'],
    bestFor: ['Parieurs réguliers', 'Fans d’événements', 'Ceux qui aiment les promos “calendrier”'],
    pros: [
      'Promos qui peuvent être très intéressantes sur les temps forts',
      'Ambiance et profondeur : bon feeling “habitué”',
      'Bon suivi quand tout est validé',
    ],
    cons: [
      'Plus “promo-driven” : il faut lire les conditions',
      'Temps de retrait variable selon méthode',
    ],
    charts: {
      oddsBySport: [
        { label: 'Foot', score: 84 },
        { label: 'Tennis', score: 82 },
        { label: 'Rugby', score: 78 },
        { label: 'Hand', score: 79 },
        { label: 'eSport', score: 75 },
      ],
      payoutTimeline: [
        { label: 'Demande', hours: 0 },
        { label: 'Traitement', hours: 10 },
        { label: 'Validation', hours: 22 },
        { label: 'Crédité', hours: 36 },
      ],
      uxScore: 86,
      trustScore: 87,
      promoScore: 88,
    },
    links: {
      official: 'https://www.genybet.fr/',
    },
    updatedAtISO: '2025-12-17',
  },
  {
    slug: 'zeturf',
    brand: 'ZEbet / ZEturf',
    tagline: 'Turf à la française, rythme rapide, promos ciblées',
    heroPitch:
      'Si le turf est ton terrain, ZEturf est un choix direct : navigation efficace, promos ciblées et une approche très orientée courses.',
    imagePath: '/images/offers/zeturf.webp',
    rating: 4.1,
    welcome: {
      headline: 'Offre de bienvenue orientée turf (selon périodes)',
      details:
        'Plutôt que de promettre “toujours plus”, ZEturf est souvent dans le ciblé. Idéal si tu sais exactement ce que tu veux jouer.',
      maxEur: 80,
      minDepositEur: 10,
      steps: [
        'Créer un compte et vérifier ton identité',
        'Déposer (souvent 10€ suffisent)',
        'Activer l’offre et jouer sur les courses éligibles',
      ],
    },
    payouts: {
      speedLabel: 'Rapide quand le KYC est validé',
      typicalHours: 24,
      methods: ['Carte bancaire', 'Virement', 'Solutions en ligne (selon dispo)'],
    },
    sports: ['Turf', 'Courses', 'Quinté+', 'Pick', 'Multi'],
    bestFor: ['Fans de courses', 'Joueurs “turf only”', 'Ceux qui aiment les promos ciblées'],
    pros: ['Parcours très orienté turf', 'Promos souvent “spéciales courses”', 'Navigation efficace'],
    cons: [
      'Moins polyvalent si tu veux uniquement du sport “classique”',
      'Offres variables selon calendrier',
    ],
    charts: {
      oddsBySport: [
        { label: 'Turf', score: 86 },
        { label: 'Quinté+', score: 84 },
        { label: 'Multi', score: 80 },
        { label: 'Pick', score: 79 },
        { label: 'Live', score: 76 },
      ],
      payoutTimeline: [
        { label: 'Demande', hours: 0 },
        { label: 'Traitement', hours: 6 },
        { label: 'Validation', hours: 14 },
        { label: 'Crédité', hours: 24 },
      ],
      uxScore: 84,
      trustScore: 86,
      promoScore: 80,
    },
    links: {
      official: 'https://www.zeturf.fr/',
    },
    updatedAtISO: '2025-12-17',
  },
];

export function getOffer(slug: string) {
  return offers.find((o) => o.slug === slug);
}


